import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import BaseDimensionDrilldownPopover from '../../components/reportsWidgets/BaseDimensionDrilldownPopover';
import withTranslate from '../widgets/withTranslate';
import { getUserData } from '../../selectors/user';
import { defaultReportQueryParams, getDrilldownDimensions, mapToCreativesFilters } from '../../utils/reports';
import { getImpersonationFromQuery, serializeReportQuery } from '../../utils/url';
import { trackMixpanelEvent, REPORTS_EVENT_PREFIX } from '../../utils/general';
import { CUSTOMER_TIERS } from '../../utils/consts';
import { IN_OPERATOR } from '../../utils/reportsConsts';
import { METRIC_COHORT_SEPARATOR } from '../../customDashboards/utils';

// Defaults the Reports page applies to every query (see fieldPresets.commonDefaults and the
// reportConfig overrides in selectors/reports.js). The drilldown URL must carry these or the
// destination report runs unfiltered on attribution type / append tables / druid mode and
// produces different metric numbers than the widget.
const REPORT_QUERY_DEFAULTS = {
    source_attribution_type: ['dynamic'],
    with_append_tables: ['with_append'],
    druid_aggregated_data_sources_mode: ['not_aggregated'],
    skan_modeled_cohort_periods: [],
};

export function buildDashboardDrilldownRedirectQuery(
    widgetQuery,
    nodeData,
    dimensionField,
    reportDates,
    availableFilters = [],
    displayUnenriched = false
) {
    if (!widgetQuery) {
        return '';
    }

    const actualWidgetQuery = widgetQuery;
    // Start with the widget's original query structure
    // Convert widget filters format to reports format
    const reportFilters = (actualWidgetQuery.filters || []).map(filter => ({
        dimension: filter.field,
        operator: filter.operator,
        values: [...(filter.values || [])],
    }));

    const regularMetrics = [];
    const cohortMetricsSeen = new Set();
    const cohortMetrics = [];
    const cohortPeriods = new Set();

    (actualWidgetQuery.metrics || []).forEach(metric => {
        if (metric.includes(METRIC_COHORT_SEPARATOR)) {
            const parts = metric.split(METRIC_COHORT_SEPARATOR);
            const period = parts[parts.length - 1];
            const metricName = parts.slice(0, -1).join(METRIC_COHORT_SEPARATOR);

            // The widget stores `<metric>__<period>` for each (metric, period) pair, so the same
            // base metric appears once per period. Reports drilldown URLs list each base metric
            // only once — match that to avoid duplicate columns and inflated cohort metric counts.
            if (!cohortMetricsSeen.has(metricName)) {
                cohortMetricsSeen.add(metricName);
                cohortMetrics.push(metricName);
            }
            cohortPeriods.add(period);
        } else {
            regularMetrics.push(metric);
        }
    });

    const deserializedReportQuery = {
        ...defaultReportQueryParams,
        ...REPORT_QUERY_DEFAULTS,
        // Override with widget-specific values
        dimensions: [...(actualWidgetQuery.dimensions || [])],
        metrics: regularMetrics,
        cohort_metrics: cohortMetrics,
        cohort_periods: Array.from(cohortPeriods),
        filters: reportFilters,
        display_unenriched: displayUnenriched,
        ...(reportDates && {
            start_date: reportDates.startDate || '',
            end_date: reportDates.endDate || '',
            ...(reportDates.startDate2 && { start_date_2: reportDates.startDate2 }),
            ...(reportDates.endDate2 && { end_date_2: reportDates.endDate2 }),
        }),
    };

    // Add widget-specific fields if they exist (some widgets might not have these)
    if (actualWidgetQuery.dataTypeFields && actualWidgetQuery.dataTypeFields.length > 0) {
        deserializedReportQuery.dataTypeFields = [...actualWidgetQuery.dataTypeFields];
    }
    if (actualWidgetQuery.creativeGroupBy && actualWidgetQuery.creativeGroupBy.length > 0) {
        deserializedReportQuery.creativeGroupBy = [...actualWidgetQuery.creativeGroupBy];
    }
    if (actualWidgetQuery.skanDateDimensionName) {
        deserializedReportQuery.skanDateDimensionName = actualWidgetQuery.skanDateDimensionName;
    }

    // Mirrors buildDrilldownRedirectQuery: only backend-filterable dimensions become query-level
    // `in` filters; everything else has to go into post_process_filters_ui or the resulting report
    // runs with strict backend filters and returns numbers that disagree with the source widget.
    (actualWidgetQuery.dimensions || []).forEach(dimension => {
        if (!nodeData[dimension]) {
            return;
        }
        const isFilterable = !!availableFilters.find(availableFilter => availableFilter.name === dimension);
        const existingFilter = deserializedReportQuery.filters.find(
            filter => filter.dimension === dimension && filter.operator === IN_OPERATOR
        );

        if (existingFilter) {
            if (!existingFilter.values.includes(nodeData[dimension])) {
                existingFilter.values.push(nodeData[dimension]);
            }
        } else if (isFilterable) {
            deserializedReportQuery.filters.push({
                dimension,
                operator: IN_OPERATOR,
                values: [nodeData[dimension]],
            });
        } else {
            deserializedReportQuery.post_process_filters_ui = deserializedReportQuery.post_process_filters_ui || {};
            deserializedReportQuery.post_process_filters_ui[dimension] = {
                filter: nodeData[dimension],
                type: 'contains',
            };
        }
    });

    // Add the new dimension being drilled down to
    deserializedReportQuery[dimensionField.type] = deserializedReportQuery[dimensionField.type] || [];

    if (dimensionField.path === '/creatives') {
        deserializedReportQuery.filters = mapToCreativesFilters(deserializedReportQuery.filters);
        deserializedReportQuery.creativeGroupBy = ['image'];
        deserializedReportQuery.display_alignment = true;
        deserializedReportQuery.api_version = 1;
        deserializedReportQuery.displayNonGraphical = true;
    } else if (dimensionField.values) {
        deserializedReportQuery[dimensionField.type].push(...dimensionField.values);
    } else {
        deserializedReportQuery[dimensionField.type] =
            dimensionField.type === 'time_breakdown'
                ? [dimensionField.name]
                : deserializedReportQuery[dimensionField.type].concat(dimensionField.name);
    }

    return serializeReportQuery(deserializedReportQuery, null, null, null, null);
}

function DashboardDimensionDrilldownPopover({
    widgetMetadata = null,
    widgetQuery = null,
    reportDates = null,
    availableFilters = [],
    displayUnenriched = false,
    nodeData = null,
    anchorEl = null,
    showPopover = true,
    closePopover = () => {},
    translate = () => {},
}) {
    const userData = useSelector(getUserData);

    // Use widget metadata instead of reports state
    const metadata = widgetMetadata || {};
    const isStandardAnalytics = userData.organization_tier === CUSTOMER_TIERS.STANDARD_ANALYTICS;

    // Generate dimension list using the same logic as the reports selector
    // Filter by dashboardVisible flag and exclude dimensions already in the widget
    const availableDrilldownDimensions = getDrilldownDimensions(metadata).filter(
        ({ name, values = [], dashboardVisible = true }) => {
            if (!dashboardVisible) return false;
            return !(metadata[name] || values.find(value => metadata[value]));
        }
    );

    const dimensionList = isStandardAnalytics
        ? availableDrilldownDimensions.filter(dimension => dimension.standardAnalyticsVisible)
        : availableDrilldownDimensions;

    const handleDimensionSelection = (dimensionField, nodeData) => {
        if (!widgetQuery) {
            return;
        }

        // Use our custom dashboard redirect query builder
        const redirectQuery = buildDashboardDrilldownRedirectQuery(
            widgetQuery,
            nodeData,
            dimensionField,
            reportDates,
            availableFilters,
            displayUnenriched
        );

        // Build URL following the same pattern as other utilities
        const redirectPageName = dimensionField.path ? dimensionField.path.substring(1) : 'react/reports'; // Remove leading slash if present
        const { href, hash } = window.location;
        const redirectHref = href.substring(0, href.indexOf(hash) + 1);

        // Check for impersonation parameter
        const impersonationUser = getImpersonationFromQuery();
        let redirectUrl = `${redirectHref}/${redirectPageName}?${redirectQuery}`;

        if (impersonationUser) {
            redirectUrl += `&user=${encodeURIComponent(impersonationUser)}`;
        }

        trackMixpanelEvent(REPORTS_EVENT_PREFIX, 'Drilldown dimension clicked', {
            widget_query: widgetQuery,
            drilldown_dimension: dimensionField.display_name,
        });

        window.open(redirectUrl, '_blank');
    };

    return (
        <BaseDimensionDrilldownPopover
            dimensionList={dimensionList}
            nodeData={nodeData}
            anchorEl={anchorEl}
            showPopover={showPopover}
            closePopover={closePopover}
            onDimensionSelection={handleDimensionSelection}
            translate={translate}
        />
    );
}

DashboardDimensionDrilldownPopover.propTypes = {
    widgetMetadata: PropTypes.any,
    widgetQuery: PropTypes.any,
    reportDates: PropTypes.any,
    availableFilters: PropTypes.arrayOf(PropTypes.any),
    displayUnenriched: PropTypes.bool,
    nodeData: PropTypes.any,
    showPopover: PropTypes.bool,
    anchorEl: PropTypes.instanceOf(Element),
    closePopover: PropTypes.func,
    translate: PropTypes.func,
};

export default withTranslate(DashboardDimensionDrilldownPopover);
